import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  Collapse,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { listPartnerDominios, listPlatformDominios } from "../api/client";
import { openWorkspaceInApp } from "../utils/openWorkspaceInApp";
import { resolveTenantDatabaseName } from "../utils/resolveTenantDatabaseName";
import { getActiveWorkspaceSlug, normalizeWorkspaceSlug } from "../utils/workspaceStorage";
import { SESSION_ROLE_STRIPE_HEIGHT } from "../utils/sessionRoleIndicator";
import { useWorkspaceConfig } from "../context/WorkspaceConfigContext";
import { PARTNER_CONSOLE_PATH } from "../pages/Partner/PartnerConsole/partnerConsoleRoutes";
import { PLATFORM_CONSOLE_PATH } from "../pages/Platform/XprobaseConsole/platformConsoleRoutes";

function workspaceLabel(row) {
  const host = String(row?.dominio || "").trim();
  const bd = String(row?.bd || "").trim();
  if (host) return host;
  if (bd) return bd;
  return row?.id != null ? `#${row.id}` : "—";
}

/**
 * Faixa colorida da sessão (partner/master) com seta que abre atalho para trocar de workspace.
 */
export default function SessionRoleWorkspaceSwitcher({ sessionRole }) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { companyKey } = useWorkspaceConfig();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [loadedOnce, setLoadedOnce] = useState(false);

  const isPartner = sessionRole?.key === "partner";
  const accent = sessionRole?.accent || "#9c27b0";
  const activeSlug = useMemo(
    () => getActiveWorkspaceSlug() || normalizeWorkspaceSlug(companyKey),
    [companyKey, open]
  );

  const loadWorkspaces = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = isPartner
        ? await listPartnerDominios({ limit: 500 })
        : await listPlatformDominios({ limit: 500, tenantLegacyStats: "0" });
      const rows = Array.isArray(res?.items)
        ? res.items
        : Array.isArray(res)
          ? res
          : [];
      // Partner/admin já listam só ativos; `excluido` pode vir omitido — não filtrar com === 0 em undefined.
      setItems(rows.filter((r) => Number(r?.excluido || 0) !== 1));
      setLoadedOnce(true);
    } catch {
      setItems([]);
      setError(
        t("topbar.workspaceSwitcher.loadError", {
          defaultValue: "Não foi possível carregar os workspaces.",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [isPartner, t]);

  useEffect(() => {
    if (open && !loadedOnce) {
      loadWorkspaces();
    }
  }, [open, loadedOnce, loadWorkspaces]);

  const filtered = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return items;
    return items.filter((row) => {
      const label = workspaceLabel(row).toLowerCase();
      const bd = String(row?.bd || "").toLowerCase();
      return label.includes(q) || bd.includes(q);
    });
  }, [items, query]);

  const handleToggle = (e) => {
    e.stopPropagation();
    setOpen((v) => !v);
  };

  const handleClose = () => {
    setOpen(false);
    setQuery("");
  };

  const handleSelect = (row) => {
    const slug = resolveTenantDatabaseName(row?.dominio, row?.bd);
    if (!slug) return;
    handleClose();
    // Nova aba por workspace (não troca a aba atual) — várias abas em paralelo.
    openWorkspaceInApp(navigate, row?.bd, row?.dominio);
  };

  const handleOpenConsoleList = () => {
    handleClose();
    navigate(isPartner ? PARTNER_CONSOLE_PATH.workspaces : PLATFORM_CONSOLE_PATH.workspaces);
  };

  if (!sessionRole) return null;

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={{ position: "relative", width: "100%", flexShrink: 0 }}>
        <Tooltip
          title={
            open
              ? t("topbar.workspaceSwitcher.hide", { defaultValue: "Ocultar troca de workspace" })
              : t("topbar.workspaceSwitcher.show", { defaultValue: "Trocar de workspace" })
          }
        >
          <Box
            role="button"
            tabIndex={0}
            aria-expanded={open}
            aria-label={t("topbar.workspaceSwitcher.show", { defaultValue: "Trocar de workspace" })}
            onClick={handleToggle}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleToggle(e);
              }
            }}
            sx={{
              height: SESSION_ROLE_STRIPE_HEIGHT,
              width: "100%",
              bgcolor: accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: (th) =>
                `inset 0 -1px 0 ${th.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)"}`,
              "&:hover": { filter: "brightness(1.06)" },
            }}
          >
            <IconButton
              size="small"
              tabIndex={-1}
              disableRipple
              sx={{
                p: 0,
                color: "#fff",
                pointerEvents: "none",
                "& .MuiSvgIcon-root": { fontSize: 14 },
              }}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Box>
        </Tooltip>

        <Collapse in={open} timeout={220} unmountOnExit>
          <Paper
            elevation={6}
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 1300,
              borderRadius: 0,
              borderTop: (th) => `1px solid ${th.palette.divider}`,
              bgcolor: "background.paper",
              maxHeight: { xs: "55vh", sm: 360 },
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box sx={{ px: 1.5, pt: 1.25, pb: 1, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                {t("topbar.workspaceSwitcher.title", { defaultValue: "Trocar workspace" })}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                {t("topbar.workspaceSwitcher.openInNewTabHint", {
                  defaultValue: "Cada workspace abre em uma aba do navegador (sem fechar as outras).",
                })}
              </Typography>
              {activeSlug ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  {t("topbar.workspaceSwitcher.current", {
                    slug: activeSlug,
                    defaultValue: "Atual: {{slug}}",
                  })}
                </Typography>
              ) : null}
              <TextField
                size="small"
                fullWidth
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("topbar.workspaceSwitcher.search", {
                  defaultValue: "Buscar workspace…",
                })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
              {loading ? (
                <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
                  <CircularProgress size={24} />
                </Box>
              ) : error ? (
                <Typography variant="body2" color="error" sx={{ p: 2 }}>
                  {error}
                </Typography>
              ) : filtered.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  {t("topbar.workspaceSwitcher.empty", {
                    defaultValue: "Nenhum workspace encontrado.",
                  })}
                </Typography>
              ) : (
                <List dense disablePadding>
                  {filtered.map((row) => {
                    const slug = resolveTenantDatabaseName(row?.dominio, row?.bd);
                    const selected = Boolean(slug && activeSlug && slug === activeSlug);
                    return (
                      <ListItemButton
                        key={row.id ?? slug ?? workspaceLabel(row)}
                        selected={selected}
                        onClick={() => handleSelect(row)}
                        disabled={!slug}
                      >
                        <ListItemText
                          primary={workspaceLabel(row)}
                          secondary={slug && slug !== workspaceLabel(row) ? slug : null}
                          primaryTypographyProps={{
                            variant: "body2",
                            fontWeight: selected ? 700 : 500,
                            noWrap: true,
                          }}
                          secondaryTypographyProps={{ variant: "caption", noWrap: true }}
                        />
                        <OpenInNewIcon sx={{ fontSize: 16, opacity: 0.55, ml: 1 }} />
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </Box>

            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                borderTop: 1,
                borderColor: "divider",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                size="small"
                onClick={handleOpenConsoleList}
                sx={{ textTransform: "none", fontWeight: 600, color: accent }}
              >
                {t("topbar.workspaceSwitcher.viewAll", {
                  defaultValue: "Ver lista completa",
                })}
              </Button>
            </Box>
          </Paper>
        </Collapse>
      </Box>
    </ClickAwayListener>
  );
}
